import React, { useState, useEffect, useRef } from "react";
import { Button, Input, Space, Table, message } from "antd";
import { SearchOutlined, ExpandAltOutlined } from "@ant-design/icons";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Highlighter from "react-highlight-words";
import Stats from "../tester-teamlead/tester-stats";
import Profile from "../tester-teamlead/profile";
import "./testerStatisticsV2.css";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const apiUrl = process.env.REACT_APP_API_TesterStatistics;

const TesterStatisticsV2 = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [testers, setTesters] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [searchedColumn, setSearchedColumn] = useState("");
  const [selectedTester, setSelectedTester] = useState(null);
  const [loading, setLoading] = useState(true);
  const searchInput = useRef(null);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error("Failed to fetch testers");
      const data = await response.json();
      setTesters(data && data.length > 0 ? data : []);
    } catch (error) {
      console.error("Error fetching testers:", error);
      messageApi.error("Failed to fetch tester statistics");
    } finally {
      setLoading(false);
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div className="tester-stats-v2-filter" onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
        />
        <Space className="tester-stats-v2-filter-actions">
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters && handleReset(clearFilters)}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined className={filtered ? "tester-stats-v2-filter-active" : ""} />
    ),
    onFilter: (value, record) =>
      (record[dataIndex]?.toString() || "").toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "rgba(56, 189, 248, 0.3)", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text != null ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleViewStats = (record) => {
    setSelectedTester(record);
    setStatsDialogOpen(true);
  };

  const handleViewProfile = (record) => {
    setSelectedTester(record);
    setProfileDialogOpen(true);
  };

  const columns = [
    {
      title: "Tester Name",
      dataIndex: "first_name",
      key: "first_name",
      width: "25%",
      ...getColumnSearchProps("first_name"),
      sorter: (a, b) => (a.first_name || "").localeCompare(b.first_name || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Tests received this month",
      dataIndex: "valid_otps",
      key: "valid_otps",
      width: "22%",
      ...getColumnSearchProps("valid_otps"),
      sorter: (a, b) => (a.valid_otps || 0) - (b.valid_otps || 0),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Actions",
      key: "actions",
      width: "30%",
      render: (_, record) => (
        <div className="tester-stats-v2-actions">
          <Button
            type="default"
            icon={<ExpandAltOutlined />}
            onClick={() => handleViewStats(record)}
            className="tester-stats-v2-btn-view"
          >
            View Details
          </Button>
          <Button
            type="primary"
            icon={<ExpandAltOutlined />}
            onClick={() => handleViewProfile(record)}
            className="tester-stats-v2-btn-profile"
          >
            View Profile
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="tester-stats-v2">
      {contextHolder}
      <div className="tester-stats-v2-header">
        <h1 className="tester-stats-v2-title">Monthly Performance Overview</h1>
      </div>

      <div className="tester-stats-v2-card">
        <Table
          columns={columns}
          dataSource={testers}
          rowKey="id"
          loading={loading}
          locale={{ emptyText: loading ? "Loading..." : "No data" }}
          pagination={{
            showSizeChanger: true,
            defaultPageSize: 10,
            pageSizeOptions: ["5", "10", "20", "50"],
            showTotal: (total) => `Total ${total} testers`,
          }}
          className="tester-stats-v2-table"
          scroll={{ x: "max-content" }}
        />
      </div>

      {/* Stats Dialog */}
      <Dialog
        open={statsDialogOpen}
        onClose={() => setStatsDialogOpen(false)}
        TransitionComponent={Transition}
        maxWidth="md"
        fullWidth
        className="tester-stats-v2-dialog"
        PaperProps={{
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <AppBar className="tester-stats-v2-appbar" position="relative">
          <Toolbar className="tester-stats-v2-toolbar">
            <Typography variant="h6" component="div" className="tester-stats-v2-dialog-title">
              Tester Statistics Details
            </Typography>
            {selectedTester && (
              <Typography variant="body2" className="tester-stats-v2-tester-name">
                {selectedTester.first_name}
              </Typography>
            )}
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setStatsDialogOpen(false)}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <div className="tester-stats-v2-body">
          {selectedTester && <Stats id={selectedTester.id} />}
        </div>
      </Dialog>

      {/* Profile Dialog */}
      <Dialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        TransitionComponent={Transition}
        maxWidth="md"
        fullWidth
        className="tester-stats-v2-dialog"
        PaperProps={{
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <AppBar className="tester-stats-v2-appbar" position="relative">
          <Toolbar className="tester-stats-v2-toolbar">
            <Typography variant="h6" component="div" className="tester-stats-v2-dialog-title">
              Tester Profile Details
            </Typography>
            {selectedTester && (
              <Typography variant="body2" className="tester-stats-v2-tester-name">
                {selectedTester.first_name}
              </Typography>
            )}
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setProfileDialogOpen(false)}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <div className="tester-stats-v2-body">
          {selectedTester && <Profile id={selectedTester.id} />}
        </div>
      </Dialog>
    </div>
  );
};

export default TesterStatisticsV2;
